import React from "react";
import styled from "styled-components";

const Container = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 24px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const Th = styled.th`
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: top;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.status) {
      case "pending":
        return "#fff3cd";
      case "sent":
        return "#d1ecf1";
      case "completed":
        return "#d4edda";
      case "expired":
        return "#f8d7da";
      case "cancelled":
        return "#e2e3e5";
      default:
        return "#e2e3e5";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "pending":
        return "#856404";
      case "sent":
        return "#0c5460";
      case "completed":
        return "#155724";
      case "expired":
        return "#721c24";
      case "cancelled":
        return "#6c757d";
      default:
        return "#6c757d";
    }
  }};
`;

const Button = styled.button`
  background: ${(props) =>
    props.variant === "danger" ? "#dc3545" : "#007bff"};
  color: white;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 8px;

  &:hover {
    background: ${(props) =>
      props.variant === "danger" ? "#c82333" : "#0056b3"};
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6c757d;
`;

const RatingDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Star = styled.span`
  color: ${(props) => (props.filled ? "#ffc107" : "#e4e5e9")};
  font-size: 16px;
`;

const FeedbackRequestList = ({
  requests = [],
  onSendRequest,
  onCancelRequest,
  loading = false,
}) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderRating = (rating) => {
    if (!rating) return "-";

    return (
      <RatingDisplay>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} filled={star <= rating}>
            ★
          </Star>
        ))}
        <span style={{ marginLeft: "8px", fontSize: "14px" }}>
          ({rating}/5)
        </span>
      </RatingDisplay>
    );
  };

  const renderActions = (request) => {
    switch (request.status) {
      case "pending":
        return (
          <>
            <Button
              onClick={() => onSendRequest(request.id)}
              disabled={loading}
            >
              Send
            </Button>
            <Button
              variant="danger"
              onClick={() => onCancelRequest(request.id)}
              disabled={loading}
            >
              Cancel
            </Button>
          </>
        );
      case "sent":
        return (
          <span style={{ color: "#6c757d", fontSize: "12px" }}>
            Waiting for response
          </span>
        );
      case "completed":
        return (
          <span style={{ color: "#28a745", fontSize: "12px" }}>Completed</span>
        );
      default:
        return null;
    }
  };

  if (requests.length === 0) {
    return (
      <Container>
        <Title>General Feedback Requests</Title>
        <EmptyState>
          <p>No feedback requests found.</p>
          <p>Create a new general feedback request to get started.</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Title>General Feedback Requests ({requests.length})</Title>

      <Table>
        <thead>
          <tr>
            <Th>Customer</Th>
            <Th>Reference</Th>
            <Th>Status</Th>
            <Th>Requested</Th>
            <Th>Sent</Th>
            <Th>Response</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {requests.map((request) => (
            <tr key={request.id}>
              <Td>
                <div>
                  <strong>{request.customerName || "N/A"}</strong>
                  <br />
                  <small style={{ color: "#6c757d" }}>{request.phone}</small>
                </div>
              </Td>
              <Td>
                <div>
                  {request.referenceType ? (
                    <>
                      <strong>{request.referenceType}</strong>
                      <br />
                      <small style={{ color: "#6c757d" }}>
                        {request.referenceId}
                      </small>
                    </>
                  ) : (
                    <span style={{ color: "#6c757d", fontStyle: "italic" }}>
                      General Experience
                    </span>
                  )}
                </div>
              </Td>
              <Td>
                <StatusBadge status={request.status}>
                  {request.status}
                </StatusBadge>
              </Td>
              <Td>
                <small>{formatDate(request.requestedAt)}</small>
              </Td>
              <Td>
                {request.sentAt ? (
                  <small>{formatDate(request.sentAt)}</small>
                ) : (
                  <span style={{ color: "#6c757d" }}>-</span>
                )}
              </Td>
              <Td>
                {request.responseRating ? (
                  <div>
                    {renderRating(request.responseRating)}
                    {request.responseComment && (
                      <div
                        style={{
                          marginTop: "4px",
                          fontSize: "12px",
                          color: "#6c757d",
                        }}
                      >
                        &ldquo;{request.responseComment.substring(0, 50)}
                        ...&rdquo;
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ color: "#6c757d" }}>-</span>
                )}
              </Td>
              <Td>{renderActions(request)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default FeedbackRequestList;
